import '@ui5/webcomponents-react';
import { DocumentType, IssueType, TranslationType, Worklist } from 'generated/graphql';

import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';

export enum NotSelectedIssueType {
  NOT_SELECTED_ISSUE_TYPE = 'NOT_SELECTED_ISSUE_TYPE'
}
export const IssueTypeOptions = { ...IssueType, ...NotSelectedIssueType };
export type IssueTypeOptions = typeof IssueTypeOptions;

export enum NotSelectedTranslationType {
  NOT_SELECTED_TRANSLATION_TYPE = 'NOT_SELECTED_TRANSLATION_TYPE'
}
export const TranslationTypeOptions = { ...TranslationType, ...NotSelectedTranslationType };
export type TranslationTypeOptions = typeof TranslationTypeOptions;

export enum NotSelectedWorklist {
  NOT_SELECTED_WORKLIST = 'NOT_SELECTED_WORKLIST'
}
export const WorklistOptions = { ...Worklist, ...NotSelectedWorklist };
export type WorklistOptions = typeof WorklistOptions;

export interface CommonBaseQueryState {
  // States
  sourceText: string;
  targetLanguage: string;
  targetText: string;
  details: string;
  selectedIssueType: IssueType | NotSelectedIssueType;
  languages: (string | undefined)[];
  // Value states
  sourceTextValueState: ValueState;
  targetLanguageValueState: ValueState;
  issueTypeValueState: ValueState;
  translationTypeValueState: ValueState;
  // Actions
  setSourceText: (value: string) => void;
  setTargetLanguage: (value: string) => void;
  setDetails: (value: string) => void;
  setSelectedIssueType: (value: IssueType) => void;
  setTargetText: (value: string) => void;
  setSourceTextValueState: (value: ValueState) => void;
  setTargetLanguageValueState: (value: ValueState) => void;
  setIssueTypeValueState: (value: ValueState) => void;
  setLanguages: (value: (string | undefined)[]) => void;
  resetAllData: () => void;
}

export interface CommonNonAbapQueryState {
  // States
  xtmProjectName: string;
  repository: string;
  collection: string;
  fileName: string;
  stringId: string;
  sourceContact: string;
  fetching: boolean;
  languagesTranslatorTeams?: string | null;
  // Value States
  xtmProjectNameValueState: ValueState;
  repositoryValueState: ValueState;
  collectionValueState: ValueState;
  fileNameValueState: ValueState;
  stringIdValueState: ValueState;
  sourceContactValueState: ValueState;
  // Actions
  setXtmProjectName: (value: string) => void;
  setRepository: (value: string) => void;
  setCollection: (value: string) => void;
  setFileName: (value: string) => void;
  setStringId: (value: string) => void;
  setSourceContact: (value: string) => void;
  setLanguagesTranslatorTeams: (value?: string) => void;
  setXtmProjectNameValueState: (value: ValueState) => void;
  setRepositoryValueState: (value: ValueState) => void;
  setCollectionValueState: (value: ValueState) => void;
  setFileNameValueState: (value: ValueState) => void;
  setStringIdValueState: (value: ValueState) => void;
  setSourceContactValueState: (value: ValueState) => void;
  resetData: () => void;
  setFetching: (value: boolean) => void;
}

export interface CommonDitaQueryState {
  // States
  xtmProjectName?: string;
  fileTitle: string;
  fileName: string;
  loio: string;
  deliverable: string;
  cmsProject: string;
  sourceContact: string;
  submap?: string;
  languagesTranslatorTeams?: string;
  fetching: boolean;
  // Value States
  fileTitleValueState: ValueState;
  fileNameValueState: ValueState;
  loioValueState: ValueState;
  deliverableValueState: ValueState;
  cmsProjectValueState: ValueState;
  sourceContactValueState: ValueState;
  // Actions
  setXtmProjectName: (value: string) => void;
  setFileTitle: (value: string) => void;
  setFileName: (value: string) => void;
  setLoio: (value: string) => void;
  setDeliverable: (value: string) => void;
  setCmsProject: (value: string) => void;
  setSourceContact: (value: string) => void;
  setSubmap: (value: string) => void;
  setLanguagesTranslatorTeams: (value?: string) => void;
  setFileTitleValueState: (value: ValueState) => void;
  setFileNameValueState: (value: ValueState) => void;
  setLoioValueState: (value: ValueState) => void;
  setDeliverableValueState: (value: ValueState) => void;
  setCmsProjectValueState: (value: ValueState) => void;
  setSourceContactValueState: (value: ValueState) => void;
  resetData: () => void;
  setFetching: (value: boolean) => void;
}

export interface CommonTrainingQueryState {
  // States
  xtmProjectName: string;
  fileName: string;
  fetching: boolean;
  // Value States
  xtmProjectNameValueState: ValueState;
  fileNameValueState: ValueState;
  // Actions
  setXtmProjectName: (value: string) => void;
  setFileName: (value: string) => void;
  setXtmProjectNameValueState: (value: ValueState) => void;
  setFileNameValueState: (value: ValueState) => void;
  resetData: () => void;
  setFetching: (value: boolean) => void;
}

export interface CommonCorporateContentQueryState {
  // States
  xtmProjectName?: string;
  fileName: string;
  fetching: boolean;
  // Value States
  fileNameValueState: ValueState;
  // Actions
  setXtmProjectName: (value?: string) => void;
  setFileName: (value: string) => void;
  setFileNameValueState: (value: ValueState) => void;
  resetData: () => void;
  setFetching: (value: boolean) => void;
}

export interface CommonABAPQueryState {
  // States
  system: string;
  objectType: string;
  objectName: string;
  objectOwner?: string | null;
  collectionPackage: string;
  collectionOwner?: string | null;
  languagesTranslatorTeams?: string | null;
  applicationComponent: string;
  repositoryProgramType: string;
  repositoryProgramId: string;
  repositoryProgramName: string;
  translationArea: string;
  fetching: boolean;
  maxLength?: number | null;
  // Value States
  systemValueState: ValueState;
  objectTypeValueState: ValueState;
  objectNameValueState: ValueState;
  objectOwnerValueState: ValueState;
  collectionPackageValueState: ValueState;
  collectionOwnerValueState: ValueState;
  applicationComponentValueState: ValueState;
  repositoryProgramTypeValueState: ValueState;
  repositoryProgramIdValueState: ValueState;
  repositoryProgramNameValueState: ValueState;
  translationAreaValueState: ValueState;
  // Actions
  setSystem: (value: string) => void;
  setObjectType: (value: string) => void;
  setObjectName: (value: string) => void;
  setObjectOwner: (value: string) => void;
  setCollectionPackage: (value: string) => void;
  setCollectionOwner: (value: string) => void;
  setLanguagesTranslatorTeams: (value: string) => void;
  setApplicationComponent: (value: string) => void;
  setRepositoryProgramType: (value: string) => void;
  setRepositoryProgramId: (value: string) => void;
  setRepositoryProgramName: (value: string) => void;
  setMaxLength: (value: number | null) => void;
  setTranslationArea: (value: string) => void;
  setSystemValueState: (value: ValueState) => void;
  setObjectTypeValueState: (value: ValueState) => void;
  setObjectNameValueState: (value: ValueState) => void;
  setObjectOwnerValueState: (value: ValueState) => void;
  setCollectionPackageValueState: (value: ValueState) => void;
  setApplicationComponentValueState: (value: ValueState) => void;
  setRepositoryProgramTypeValueState: (value: ValueState) => void;
  setRepositoryProgramIdValueState: (value: ValueState) => void;
  setRepositoryProgramNameValueState: (value: ValueState) => void;
  setTranslationAreaValueState: (value: ValueState) => void;
  setFetching: (value: boolean) => void;
  resetData: () => void;
}

export interface CommonNotesQueryState {
  // States
  documentNumber: string;
  component: string;
  documentType: DocumentType;
  worklist?: Worklist | NotSelectedWorklist | null;
  documentResponsible?: string | null;
  fetching: boolean;
  // Value states
  documentNumberValueState: ValueState;
  componentValueState: ValueState;
  documentTypeValueState: ValueState;
  worklistValueState: ValueState;
  documentResponsibleValueState: ValueState;
  // Actions
  setDocumentNumber: (value: string) => void;
  setComponent: (value: string) => void;
  setDocumentType: (value: DocumentType) => void;
  setWorklist: (value?: Worklist | NotSelectedWorklist | null) => void;
  setDocumentResponsible: (value: string) => void;
  setDocumentNumberValueState: (value: ValueState) => void;
  setComponentValueState: (value: ValueState) => void;
  setDocumentTypeValueState: (value: ValueState) => void;
  setWorklistValueState: (value: ValueState) => void;
  setDocumentResponsibleValueState: (value: ValueState) => void;
  resetData: () => void;
  setFetching: (value: boolean) => void;
}

export interface CommonMarketingQueryState {
  // States
  account?: string;
  fileName?: string;
  fetching: boolean;
  // Actions
  setAccount: (value?: string) => void;
  setFileName: (value?: string) => void;
  resetData: () => void;
  setFetching: (value: boolean) => void;
}
